import React from 'react'

/**
 * ButtonGroup component - segmented control for filtering or selection
 * Provides a pill-like appearance with seamless button integration
 * Supports custom options, active state, and onChange callback
 */
const ButtonGroup = ({
  options = [],
  value,
  onChange,
  className = '',
  size = 'medium',
  variant = 'default',
  disabled = false,
  fullWidth = false,
  rounded = 'lg',
  ...props
}) => {
  // Size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'xs':
        return 'px-2 py-1 text-xs'
      case 'small':
        return 'px-3 py-1 text-sm'
      case 'large':
        return 'px-6 py-3 text-base'
      case 'xl':
        return 'px-8 py-4 text-lg'
      default: // medium
        return 'px-4 py-2 text-sm'
    }
  }

  // Variant classes
  const getVariantClasses = () => {
    switch (variant) {
      case 'outline':
        return 'bg-white border border-gray-300'
      case 'solid':
        return 'bg-gray-800 text-white'
      case 'primary':
        return 'bg-blue-100'
      case 'success':
        return 'bg-green-100'
      case 'warning':
        return 'bg-yellow-100'
      case 'danger':
        return 'bg-red-100'
      default: // default
        return 'bg-gray-100'
    }
  }

  // Handle option click
  const handleOptionClick = (option) => {
    if (disabled || !onChange) return
    onChange(option)
  }

  // Check if option is active
  const isActive = (option) => {
    if (typeof value === 'string') {
      return value === option
    }
    if (typeof value === 'object' && value !== null) {
      return value.value === option.value || value.key === option.key
    }
    return false
  }

  // Get display value
  const getDisplayValue = (option) => {
    if (typeof option === 'string') return option
    if (typeof option === 'object' && option !== null) {
      return option.label || option.name || option.value || option.key
    }
    return String(option)
  }

  // Get option value
  const getOptionValue = (option) => {
    if (typeof option === 'string') return option
    if (typeof option === 'object' && option !== null) {
      return option.value || option.key
    }
    return option
  }

  if (!options || options.length === 0) {
    return null
  }

  // Rounded corner classes
  const getRoundedClasses = () => {
    switch (rounded) {
      case 'none':
        return 'rounded-none'
      case 'sm':
        return 'rounded-sm'
      case 'md':
        return 'rounded-md'
      case 'lg':
        return 'rounded-lg'
      case 'xl':
        return 'rounded-xl'
      case 'full':
        return 'rounded-full'
      default:
        return 'rounded-lg'
    }
  }

  return (
    <div 
      className={`${fullWidth ? 'w-full' : 'inline-flex'} bg-gray-100 ${getRoundedClasses()} p-1 ${className}`}
      role="group"
      aria-label="Button group"
      {...props}
    >
      {options.map((option, index) => {
        const optionValue = getOptionValue(option)
        const displayValue = getDisplayValue(option)
        const active = isActive(option)
        const isFirst = index === 0
        const isLast = index === options.length - 1

        return (
          <button
            key={index}
            type="button"
            onClick={() => handleOptionClick(option)}
            disabled={disabled}
            className={`
              ${getSizeClasses()}
              font-medium transition-all duration-200 ease-in-out
              ${active 
                ? 'bg-white text-gray-900 shadow-sm font-semibold' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              ${isFirst ? 'rounded-l-md' : ''}
              ${isLast ? 'rounded-r-md' : ''}
              ${!isFirst && !isLast ? '' : ''}
              ${active && isFirst ? 'rounded-l-md' : ''}
              ${active && isLast ? 'rounded-r-md' : ''}
              ${active && !isFirst && !isLast ? '' : ''}
            `.trim()}
            aria-pressed={active}
          >
            {displayValue}
          </button>
        )
      })}
    </div>
  )
}

export default ButtonGroup
